import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { id: "services", label: "Services" },
  { id: "about", label: "About" },
  { id: "industries", label: "Industries" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (l) => {
    setOpen(false);
    scrollToId(typeof l === "string" ? l : l.id);
  };

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-all duration-500 ${
          scrolled
            ? "border-white/10 bg-obsidian/85"
            : "border-transparent bg-obsidian/40"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo />

          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l)}
                className="group relative font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400 transition-colors duration-300 hover:text-white"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-cta-contact"
              onClick={() => go("contact")}
              className="group relative hidden items-center gap-2.5 rounded-full bg-gold px-5 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-obsidian transition-all duration-300 hover:bg-gold-hover hover:shadow-[0_0_32px_rgba(226,185,92,0.45)] sm:flex"
            >
              <span className="relative flex h-2 w-2">
                <span className="ping-ring absolute inline-flex h-full w-full rounded-full bg-obsidian" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-obsidian" />
              </span>
              Request a PM
            </button>

            <button
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
              onClick={() => setOpen((v) => !v)}
              className="grid h-11 w-11 place-items-center rounded-lg border border-white/10 bg-plate/60 text-slate-200 lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-obsidian/97 px-8 backdrop-blur-2xl lg:hidden"
          >
            <div className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => go(l)}
                  className="border-b border-white/8 py-5 text-left font-display text-3xl font-bold tracking-tight text-slate-100 transition-colors hover:text-gold"
                >
                  <span className="mr-4 font-mono text-xs text-gold">0{i + 1}</span>
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                onClick={() => go("contact")}
                className="mt-8 flex items-center justify-center gap-3 rounded-full bg-gold px-6 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-obsidian"
              >
                <span className="h-2 w-2 rounded-full bg-obsidian blink-dot" />
                Request a PM
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
